/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl, getConfig } from "@/company-switcher/api";
import type { Company, CompanyOption, CustomerCompanyInfo, GraphQLResponse } from '@/company-switcher/types/company';
import { GET_CUSTOMER_COMPANY_CONTEXT } from './graphql/customerCompanyContext.graphql';
import { GET_CUSTOMER_GROUP_CONTEXT } from './graphql/customerGroupContext.graphql';

export class CustomerCompanyContext {
  private static instance: CustomerCompanyContext;

  // Constants
  private readonly EMPTY_CUSTOMER_COMPANY_CONTEXT: CustomerCompanyInfo = {
    currentCompany: { id: '', name: '' },
    customerCompanies: [],
    customerGroupId: '',
  };

  private cache: CustomerCompanyInfo | null = null;

  /**
   * Get singleton instance
   */
  static getInstance(): CustomerCompanyContext {
    return CustomerCompanyContext.instance ??= new CustomerCompanyContext();
  }

  /**
   * Transforms a company object into a company option for UI components
   */
  private transformCompanyToOption = (company: Company): CompanyOption => ({
    text: company.name,
    value: company.id
  });

  /**
   * Decodes base64 string and returns SHA1 hash
   */
  private async processCustomerGroupId(base64String: string): Promise<string> {
    const decoded = Uint8Array.from(atob(base64String), c => c.charCodeAt(0));
    const hashBuffer = await crypto.subtle.digest('SHA-1', decoded);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Checks if the user is authenticated by verifying the Authorization header
   */
  private isUserAuthenticated(): boolean {
    return Boolean(getConfig().fetchGraphQlHeaders?.Authorization);
  }

  resetCache(): void {
    this.cache = null;
  }

  /**
   * Fetches and updates only the customer group information in the cache
   * 
   * @returns Promise containing the updated customer group ID
   * @throws Will not throw errors - returns null on failure
   */
  async updateCustomerGroup(): Promise<string | null> {
    if (!this.isUserAuthenticated()) {
      return null;
    }

    try {
      const response = await fetchGraphQl<{ customerGroup: { uid: string } }>(GET_CUSTOMER_GROUP_CONTEXT);
      if (response.errors) {
        return null;
      }
      const customerGroupId = await this.processCustomerGroupId(response.data.customerGroup.uid);
      if (this.cache) {
        this.cache.customerGroupId = customerGroupId;
      }
      return customerGroupId;
    } catch (e) {
      console.error(e)
      return null;
    }
  }

  /**
   * Fetches customer company information including the current company and all available companies
   * 
   * @returns Promise containing current company and list of available companies
   * @throws Will not throw errors - returns empty data on failure
   */
  async getCustomerCompanyInfo(pageSize = 100): Promise<CustomerCompanyInfo> {
    // Return cached data if available
    if (this.cache) {
      return this.cache;
    }

    // Return empty data if user is not authenticated
    if (!this.isUserAuthenticated()) {
      return this.EMPTY_CUSTOMER_COMPANY_CONTEXT;
    }

    try {
      // Fetch company data from GraphQL API
      const response = (await fetchGraphQl<GraphQLResponse>(GET_CUSTOMER_COMPANY_CONTEXT, { variables: { pageSize } }));
      if (response.errors) {
        return this.EMPTY_CUSTOMER_COMPANY_CONTEXT;
      }

      const data = response.data;
      const customerGroup = await this.processCustomerGroupId(response.data.customerGroup.uid);
      // Whitelist approach: only allow APPROVED and BLOCKED companies
      // BLOCKED companies allow users to log in and view but not perform actions
      const activeCompanies = data.customer.companies.items.filter(
        company => company.status === 'APPROVED' || company.status === 'BLOCKED'
      );
      const customerCompanies = activeCompanies.map(this.transformCompanyToOption);

      this.cache = {
        currentCompany: data.company,
        customerCompanies,
        customerGroupId: customerGroup
      }
      return this.cache;
    } catch (e) {
      console.error(e);
      return this.EMPTY_CUSTOMER_COMPANY_CONTEXT;
    }
  }
}

export const getCustomerCompanyInfo = (pageSize = 100): Promise<CustomerCompanyInfo> => {
  return CustomerCompanyContext.getInstance().getCustomerCompanyInfo(pageSize);
};

export const updateCustomerGroup = (): Promise<string | null> => {
  return CustomerCompanyContext.getInstance().updateCustomerGroup();
};